import type { ComponentType } from "react";
import type { IconName } from "@salestrak/ui";

import type { AutomationConfigField } from "#app/hooks/automations/use-automation-catalog";
import type { VariableGroup } from "#app/components/automations/canvas/automation-variables";
import { updateFieldStepUI } from "#app/components/automations/canvas/steps/update-field";
import { webhookStepUI } from "#app/components/automations/canvas/steps/webhook";
import type { Omittable } from "@salestrak/ui";

// ---------------------------------------------------------------------------
// The client-side step-type UI registry.
//
// The server's catalog (apps/api/core/src/automations/catalog/step-types.ts)
// is the source of truth for WHICH step types exist and their config schemas;
// every canvas surface renders unknown types generically from that schema —
// a new server-registered action (e.g. an integration's) gets a working
// picker row, node card, and config panel with ZERO client changes.
//
// This registry carries the OPTIONAL client-side polish per type. Adding a
// node with custom UI is: one module under `canvas/steps/<type>.tsx`
// exporting its `StepUIDef`, plus one entry here. Nothing else — the panel,
// summaries, pickers, and go-live preview all consult this map.
// ---------------------------------------------------------------------------

// Props a custom field control receives — the same surface the generic
// renderers work from, so a per-node module can do anything the built-ins
// can (including calling its own data hooks). The component owns the WHOLE
// field block (render your own `FieldRow`s), which lets one control span
// multiple schema fields (e.g. Update field's picker + typed value).
export type StepFieldControlProps = {
  field: AutomationConfigField;
  config: Record<string, unknown>;
  /** The trigger's noun ("Project"), for subject-adapting copy. */
  subject: string;
  /** Variables referenceable from this step (templatable inputs). */
  variableGroups: VariableGroup[];
  onConfigChange: (config: Record<string, unknown>) => void;
};

export type StepUIDef = Omittable<{
  /**
   * Canvas display label ("Update stage" instead of the catalog's unscoped
   * "Move stage"); `{subject}` resolves to the trigger's noun. Falls back to
   * the catalog label.
   */
  label?: string;
  /**
   * Section header in the action-type dropdown; `{subject}` substituted.
   * Types without a group list last, ungrouped — a new server action still
   * shows up before it gets a registry entry.
   */
  group?: string;
  /**
   * The preposition joining the action label to its primary reference in text
   * summaries ("Update stage" → to → "Won"). Omit when the label + reference
   * read as a phrase on their own ("Create Project <workflow>").
   */
  connective?: string;
  /**
   * Per-config-field overrides: a component replaces the generic renderer for
   * that field (and owns its whole block); `"hidden"` suppresses a field that
   * another override renders (e.g. Update field's `value`, webhook's raw
   * `payload`).
   */
  fields?: Record<string, ComponentType<StepFieldControlProps> | "hidden">;
  /**
   * Presentation tweaks for generically-rendered fields — a custom placeholder
   * and/or a label-less block for fields that ARE the whole action (the Add
   * comment body).
   */
  fieldPresentation?: Record<string, { placeholder?: string; hideLabel?: boolean }>;
  /**
   * Restricts the node card's config caption to these schema fields. For a
   * step whose remaining config is machinery a one-line caption can't read —
   * the webhook card shows its endpoint, never the payload mode/body. Omit to
   * caption every filled field (the generic default).
   */
  summaryFields?: string[];
}>;

// Display order of the action dropdown's action-category headings
// (pre-substitution keys). Groups not listed here sort after these, then
// ungrouped types last.
export const STEP_GROUP_ORDER = [
  "CREATE",
  "COMMUNICATION",
  "EMAIL",
  "{subject} MANAGEMENT",
  "PLUGINS",
];

export const STEP_UI: Record<string, StepUIDef> = {
  createIssue: { label: "Create {subject}", group: "CREATE" },
  addNote: {
    label: "Add comment",
    group: "COMMUNICATION",
    fieldPresentation: {
      // The body IS the whole action — label-less, with the app's
      // comment-composer placeholder instead of a "Body" caption.
      body: { placeholder: "Comment or type '/' for commands", hideLabel: true },
    },
  },
  notify: { label: "Send direct message", group: "COMMUNICATION", connective: "to" },
  assignIssue: { label: "Update assignees", group: "{subject} MANAGEMENT", connective: "to" },
  moveIssueStage: { label: "Update stage", group: "{subject} MANAGEMENT", connective: "to" },
  archiveIssue: { label: "Archive {subject}", group: "{subject} MANAGEMENT" },
  updateIssueField: updateFieldStepUI,
  webhook: webhookStepUI,
  // Email delivery is slated to become plugin-provided, and carries its own
  // heading so the family has a place to grow into. No label override: the
  // catalog's "Send email" flows through.
  sendEmail: { group: "EMAIL" },
  // Control-flow types carry NO entry — the catalog's names ("If / Else",
  // "Filter") flow through. Bare "If" was tried and walked back: it read too
  // short as a card title; "If / Else" names the structure while the
  // True/False leg chips name the outcomes.
};

export function stepUIFor(stepType: string | undefined | null): StepUIDef | undefined {
  return stepType ? STEP_UI[stepType] : undefined;
}
